import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, RouterState } from '@angular/router';
import { environment } from '../../environments/environment';
import { EventService } from '../service/event.service';
import { ShowErrorEvent, ShowProgressEvent, SessionExpireEvent, FormSubmittedEvent, CountryMistmatchEvent, EmploymentGapEvent } from '../service/events';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { IntervalSessionService } from '../service/interval-session.service';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false; // To prevent multiple refresh calls
    private readonly refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(private readonly router: Router, private readonly eventBus: EventService, private readonly authService: AuthService,
        private readonly intervalSessionService: IntervalSessionService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.includes(environment.api_uri)) {
            if (!(req.url.includes("/uploadApplicantAttachments") || (req.url.includes("/updateAttachment")) || req.url.includes("/uploadApplicantDisputeAttachments") || req.url.includes("/getVendorBatchRequest"))) {
                this.eventBus.emit(new ShowProgressEvent({ show: true }));
            }
            req = req.clone({
                headers: this.setReqHeaders(req.headers)
            });

            this.eventBus.emit(new SessionExpireEvent({ text: "New Session" }));

            return next.handle(req).pipe(
                tap((event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        if (event.body?.submitFlag) {
                            this.eventBus.emit(new FormSubmittedEvent({ text: event.body.submitFlag }));
                        }
                        this.eventBus.emit(new ShowProgressEvent({ show: false }));
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: 0, text: '' }));
                        const x_auth_token: string = event.headers.get(environment.x_auth_token_header);
                        const x_verify_token: string = event.headers.get(environment.verify_token_header);
                        this.storeToken(environment.x_auth_token_header, x_auth_token);
                        this.storeToken(environment.verify_token_header, x_verify_token);

                        // Reset the inactivity timer
                        this.intervalSessionService.resetInactivityTimer();
                    }
                }),
                catchError((err: any) => {
                    if (err instanceof HttpErrorResponse && ([401, 403].includes(err.status))) {
                        this.eventBus.emit(new ShowProgressEvent({ show: false }));
                        if (!(req.url.includes("/uploadApplicantAttachments") || (req.url.includes("/updateAttachment")) || req.url.includes("/submitVendorBatchRequest"))) {
                            let errorText: any;

                            // Add check for ApplicationException with AFErrorCode else ApplicationException with Message
                            if (typeof err.error === 'string') {
                                errorText = err.error;
                            } else if (err?.error?.error) {
                                errorText = err.error.error;
                            } else if (err?.error?.message) {
                                errorText = err.error.message;
                            } else {
                                errorText = 'An unexpected error occurred.';
                            }

                            if (typeof err.error === 'string' && errorText.includes("AF-5")) { // Server country mismatch error code
                                this.eventBus.emit(new CountryMistmatchEvent({ show: true }));
                            } else if (typeof err.error === 'string' && errorText.includes("AF-6")) {
                                this.eventBus.emit(new EmploymentGapEvent({ show: true }));
                            } else if (err.status === 403) {
                                console.log("Session expired, refreshing token.");
                            } else if (err.status === 401 && typeof err.error === 'string' && errorText.includes("AF-1")) {
                                this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: err.error }));
                                console.log("Throw return error... Login failed, do not reattempt");
                                return throwError(() => err); // Login failed, do not reattempt
                            } else {
                                this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: err.error }));
                            }
                        }

                        const state: RouterState = this.router.routerState;
                        console.log('return state url', state.snapshot.url);

                        if (this.authService.getVerifyId()) { // Don't reattempt if the user is not logged in
                            // Handle token expiration
                            return this.handle401Error(req, next);
                        }
                    } else if (err.status === 400) {
                        const errorMessage = err.error?.message ?? 'Bad Request: Please check your input and try again.';
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: errorMessage }));
                    } else if (err.status === 500 && err?.error.includes("AF-6")) { // Employment gap error code
                        this.eventBus.emit(new EmploymentGapEvent({ show: true }));
                    } else if (err.status === 500 && err?.error.includes("AF-5")) { // Server country mismatch error code
                        this.eventBus.emit(new CountryMistmatchEvent({ show: true }));
                    } else if (err.status === 500 && err?.error.includes("AF-3")) { // General error and social mismatch in personal information
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: err.error }));
                    } else if (err.status === 500 && err?.error.includes("AF-2")) { // History gap or error while saving applicant form or dispute form
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: err.error }));
                    } else if (err.status === 500 && err?.error.includes("AF-4")) { // Address history validaiton error
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: err.error }));
                    } else {
                        // Generic error handler for unexpected HTTP errors
                        const errorMessage = 'An unexpected error occurred. Please try again later.';
                        this.eventBus.emit(new ShowErrorEvent({ httpStatus: err.status, text: errorMessage }));
                    }

                    this.eventBus.emit(new ShowProgressEvent({ show: false }));

                    return throwError(() => err);
                })
            );
        }

        return next.handle(req);
    }

    storeToken(key: string, token: string): void {
        if (token && key) {
            sessionStorage.setItem(key, token);
        }
    }

    setReqHeaders(headers: HttpHeaders): HttpHeaders {
        let xToken = sessionStorage.getItem(environment.x_auth_token_header);
        let xVerifyId = sessionStorage.getItem(environment.verify_token_header);
        headers.set('Content-Type', 'applicantion/json');
        headers.set('Accept', 'application/json');

        if (xToken) {
            // Ensure the token is always prefixed with 'applicant-form '
            if (!xToken.startsWith('applicant-form ')) {
                xToken = 'applicant-form ' + xToken;
            }

            headers = headers.set(environment.x_auth_token_header, xToken);
        }

        if (xVerifyId) {
            headers = headers.set(environment.verify_token_header, xVerifyId);
        }

        return headers;
    }

    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            console.log("Already refreshing token, waiting for it to complete.");
        }

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null); // Reset the subject

            // Call the refresh token endpoint
            return this.authService.refreshToken().pipe(
                tap((response) => {
                    this.storeToken(environment.x_auth_token_header, "applicant-form " + response.accessToken);
                    this.refreshTokenSubject.next(response.accessToken); // Update the refresh token subject
                }),
                switchMap((response) => {
                    // Retry the original request with the new token
                    req = req.clone({
                        headers: req.headers.set(environment.x_auth_token_header, "applicant-form " + response.accessToken)
                    });
                    return next.handle(req);
                }),
                catchError((err) => {
                    this.authService.logout(); // Log the user out if refresh fails
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                })
            );
        } else {
            // If a refresh is already in progress, wait for it to complete
            return this.refreshTokenSubject.pipe(
                filter((token) => token != null), // Wait until the token is not null
                take(1), // Take the next emitted value
                switchMap(() => {
                    req = req.clone({
                        headers: req.headers.set(environment.x_auth_token_header, "applicant-form " + sessionStorage.getItem(environment.x_auth_token_header))
                    });
                    return next.handle(req);
                })
            );
        }
    }
}
